/*
 * vms-mega-menu.js  -  VMS mega-menu header behavior
 * ----------------------------------------------------------------------------
 * No dependencies. All listeners are document-level and bound ONCE
 * (window.__vmsMegaBound) so they survive Dawn's sticky-header re-renders and
 * theme-editor section reloads. There is only one header on the page, so hooks
 * are class/attribute based (not section-id scoped) - safe for a static asset.
 *
 * MARKUP CONTRACT  (sections/vms-mega-header.liquid + snippets/vms-mega-menu.liquid)
 *   .vms-header                       header root (gets .is-search-open / .is-menu-open)
 *   .vms-mega                         nav + panels wrapper
 *   .vms-mega__trigger                a nav trigger; carries data-vms-panel="KEY". A plain
 *                                     <button> trigger also carries aria-expanded
 *   .vms-mega__word                   the LINK half of a split trigger (also .vms-mega__trigger):
 *                                     navigates, and never carries aria-expanded
 *   .vms-mega__chev                   the BUTTON half of a split trigger: carries
 *                                     data-vms-panel="KEY" + aria-expanded, and owns the panel
 *   .vms-mega__panel                  a mega panel;   carries data-vms-panel="KEY"; opens via .is-open
 *   .vms-mega__subtoggle              a drawer sub-accordion button inside a panel; toggles its
 *                                     own aria-expanded, which the CSS reads (no [hidden])
 *   .vms-mega__scrim                  dimming scrim behind an open panel (desktop)
 *   .vms-header__burger               mobile hamburger -> toggles .vms-header.is-menu-open
 *   .vms-header__search-toggle        mobile search icon -> toggles .vms-header.is-search-open
 *   .vms-header__search-input         the search <input> (focused when search opens)
 *
 *   Phone / "Talk to an expert" (editable hours gate):
 *   .vms-expert                       the tel: <a>. data-* attributes drive the gate:
 *       data-hours-enforce  "true" | "false"   master toggle
 *       data-hours-days     "1,2,3,4,5"         days open (0=Sun .. 6=Sat)
 *       data-hours-open     "540"               open  time, minutes since local midnight
 *       data-hours-close    "960"               close time, minutes since local midnight
 *       data-hours-tz       "America/Chicago"   IANA timezone the hours are expressed in
 *   .vms-hours-modal                  off-hours dialog (relocated to <body> on init)
 *
 *   SITE-WIDE: every OTHER a[href^="tel:"] on the page is gated too (contact
 *   page, footer, body copy, anything added later). Those links carry no
 *   data-* attributes; they borrow the schedule from the .vms-expert element
 *   above, which is the single source of truth. No markup changes needed to
 *   opt a new phone link in. Fails OPEN: if the header expert module is hidden
 *   (no schedule) or the modal is absent, the link dials as normal.
 *   .vms-hours-modal__close           its close button
 * ----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CLOSE_DELAY = 140;          // ms grace after mouseleave before a panel closes
  var ROW_SWAP_REST = 300;        // mega menu review 2026-09-27 item 2: rest before any trigger swaps an open panel
  var MOBILE_MQ = '(max-width: 819px)'; // MUST match the CSS @media drawer breakpoint
  var isMobile = function () { return window.matchMedia(MOBILE_MQ).matches; };
  /* A touch device can be as wide as any laptop, so the split trigger's tap rule is keyed
     on POINTER CAPABILITY, never on width: with no hover there is no other way to open a
     panel from the word, so the first tap opens it and only the second follows the link. */
  var COARSE_MQ = '(hover: none)';
  var isCoarse = function () { return window.matchMedia(COARSE_MQ).matches; };

  /* ------------------------------------------------------------------ *
   *  MEGA PANELS (hover/click open, single-open on desktop)
   * ------------------------------------------------------------------ */
  var closeTimer = null;
  var hoverKey = null; // mega menu review 2026-09-27 item 1

  function triggers() { return Array.prototype.slice.call(document.querySelectorAll('.vms-mega__trigger')); }
  function panels()   { return Array.prototype.slice.call(document.querySelectorAll('.vms-mega__panel')); }
  function header()   { return document.querySelector('.vms-header'); }

  /* The element that OWNS aria-expanded for a panel: the plain <button> trigger, or the
     chevron half of a split trigger. The linked word must never get the attribute, so the
     sync below cannot just walk .vms-mega__trigger. Every open/close routes through here. */
  function expanders() {
    return Array.prototype.slice.call(document.querySelectorAll('.vms-mega__trigger[aria-expanded], .vms-mega__chev'));
  }
  function expanderFor(key) {
    if (!key) return null;
    var sel = '[data-vms-panel="' + cssEscape(key) + '"]';
    return document.querySelector('.vms-mega__chev' + sel) ||
           document.querySelector('.vms-mega__trigger' + sel + '[aria-expanded]');
  }

  /* REST RULE (2026-09-22 lower row; 2026-09-27 every row): while a panel is open, another
     trigger only arms a timer; mousemove over it restarts the timer and it opens when the
     timer fires with the pointer still on it. Entering the open panel, leaving the pending
     trigger or leaving the menu cancels it; a click opens at once. */
  var pendingTrigger = null;
  var pendingTimer = null;

  function cancelPending() {
    clearTimeout(pendingTimer);
    pendingTimer = null;
    pendingTrigger = null;
  }
  function armPending(t) {
    clearTimeout(pendingTimer);
    pendingTrigger = t;
    pendingTimer = setTimeout(function () {
      var p = pendingTrigger;
      pendingTrigger = null;
      pendingTimer = null;
      pendingFire(p);
    }, ROW_SWAP_REST);
  }
  function pendingFire(p) {
    if (p && p.matches && p.matches(':hover')) { openPanel(p.getAttribute('data-vms-panel')); hoverKey = p.getAttribute('data-vms-panel'); }
  }
  // a11y O-7 2026-09-28: the chevron name is static; aria-expanded alone carries the state
  function setExp(t, on) {
    t.setAttribute('aria-expanded', on ? 'true' : 'false');
  }

  /* Lock body scroll while the drawer OR the off-hours modal is open. */
  function lockScroll() {
    var h = header();
    var drawerOpen = h && h.classList.contains('is-menu-open');
    var modal = modalEl();
    var modalOpen = modal && modal.classList.contains('is-open');
    document.documentElement.style.overflow = (drawerOpen || modalOpen) ? 'hidden' : '';
  }

  function openPanel(key) {
    cancelPending();
    clearTimeout(closeTimer);
    expanders().forEach(function (t) { setExp(t, t.getAttribute('data-vms-panel') === key); });
    panels().forEach(function (p) {
      p.classList.toggle('is-open', p.getAttribute('data-vms-panel') === key);
    });
    document.body.classList.add('vms-mega-open');
  }

  function closePanels() {
    cancelPending();
    clearTimeout(closeTimer);
    hoverKey = null;
    // if keyboard focus is inside a panel we're about to hide, return it to that
    // panel's trigger first so focus is never stranded on a display:none element
    var active = document.activeElement;
    var openP = active && active.closest && active.closest('.vms-mega__panel.is-open');
    if (openP) {
      var t = expanderFor(openP.getAttribute('data-vms-panel'));
      if (t) t.focus();
    }
    expanders().forEach(function (t) { setExp(t, false); });
    panels().forEach(function (p) { p.classList.remove('is-open'); });
    document.body.classList.remove('vms-mega-open');
  }

  /* On mobile every trigger is an independent accordion toggle. */
  function toggleAccordion(key) {
    var t = expanderFor(key);
    var p = key && document.querySelector('.vms-mega__panel[data-vms-panel="' + cssEscape(key) + '"]');
    if (!t || !p) return;
    var open = t.getAttribute('aria-expanded') === 'true';
    // single-open (match desktop): collapse all, then open the tapped one
    expanders().forEach(function (x) { setExp(x, false); });
    panels().forEach(function (x) { x.classList.remove('is-open'); });
    if (!open) {
      setExp(t, true);
      p.classList.add('is-open');
      // mega menu review 2026-09-27 item 12: a screen panel starts at the top, focus on Back
      var li = t.closest('.vms-mega__nav-item');
      var back = li && li.classList.contains('vms-mega__nav-item--screen') && p.querySelector('.vms-mega__back');
      if (back) {
        var m = document.querySelector('.vms-mega');
        if (m) m.scrollTop = 0;
        back.focus({ preventScroll: true });
      } else {
        t.scrollIntoView({ block: 'nearest' }); // keep the tapped trigger + its panel in view
      }
    }
  }

  function clickToggle(t, key) {
    if (t.getAttribute('aria-expanded') !== 'true') { openPanel(key); hoverKey = null; return; }
    if (hoverKey === key) { hoverKey = null; return; }
    closePanels();
  }

  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/["\\\]]/g, '\\$&');
  }

  /* ------------------------------------------------------------------ *
   *  PHONE / "Talk to an expert" - editable calling-hours gate
   * ------------------------------------------------------------------ */
  function isOpenNow(el) {
    if (el.getAttribute('data-hours-enforce') !== 'true') return true; // gate disabled -> always dial
    var tz   = el.getAttribute('data-hours-tz') || 'America/Chicago';
    var days = (el.getAttribute('data-hours-days') || '1,2,3,4,5')
                 .split(',').map(function (n) { return parseInt(n, 10); });
    var open  = parseInt(el.getAttribute('data-hours-open'), 10);
    var close = parseInt(el.getAttribute('data-hours-close'), 10);
    if (isNaN(open) || isNaN(close)) return true; // misconfigured -> fail open (let them call)

    try {
      var now = new Date();
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(now);
      var h = 0, m = 0, wd = '';
      parts.forEach(function (p) {
        if (p.type === 'hour') h = parseInt(p.value, 10);
        if (p.type === 'minute') m = parseInt(p.value, 10);
        if (p.type === 'weekday') wd = p.value;
      });
      if (h === 24) h = 0; // some engines render midnight as 24 in hour12:false
      var DAY = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      var day = DAY[wd];
      if (days.indexOf(day) === -1) return false;
      var mins = h * 60 + m;
      return mins >= open && mins < close;
    } catch (e) {
      return true; // Intl/timezone unsupported -> fail open
    }
  }

  /* Calling hours are configured ONCE, on the header's "Talk to an expert"
   * anchor. Every other tel: link on the site (contact page, footer, body copy)
   * borrows that schedule rather than duplicating the settings, so there is a
   * single source of truth. If the header module is hidden there is no source,
   * and every link dials normally. Fail open: never trap a customer. */
  function hoursSource() { return document.querySelector('.vms-expert[data-hours-enforce]'); }

  /* ------------------------------------------------------------------ *
   *  OFF-HOURS MODAL  (APG dialog: focus-trap, Esc, scroll-lock, return)
   * ------------------------------------------------------------------ */
  var lastFocus = null;

  function modalEl() { return document.querySelector('.vms-hours-modal'); }

  function focusable(el) {
    return Array.prototype.slice.call(el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (n) { return n.offsetWidth || n.offsetHeight || n.getClientRects().length; });
  }

  function openModal() {
    var el = modalEl(); if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    var btn = el.querySelector('.vms-hours-modal__close');
    if (btn) btn.focus();
  }

  function closeModal() {
    var el = modalEl(); if (!el) return;
    el.classList.remove('is-open');
    lockScroll(); // stays locked if the mobile drawer is still open
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  function trapTab(e) {
    var el = modalEl();
    if (!el || !el.classList.contains('is-open')) return;
    var nodes = focusable(el);
    if (!nodes.length) { e.preventDefault(); return; }
    var first = nodes[0], last = nodes[nodes.length - 1], active = document.activeElement;
    if (!el.contains(active)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }

  /* ------------------------------------------------------------------ *
   *  PER-LOAD SETUP (relocate modal to <body> so position:fixed escapes
   *  the sticky-header's transformed ancestor - PDP/V3 lesson)
   * ------------------------------------------------------------------ */
  function relocateModal() {
    var el = modalEl();
    if (el && el.parentNode !== document.body) document.body.appendChild(el);
  }

  /* ------------------------------------------------------------------ *
   *  GLOBAL, DELEGATED LISTENERS (bound once)
   * ------------------------------------------------------------------ */
  function bindOnce() {
    if (window.__vmsMegaBound) return;
    window.__vmsMegaBound = true;

    // --- pointer: open on hover (desktop), toggle on click (touch + a11y) ---
    /* Both openers are OFF for a coarse pointer: a tap synthesizes mouseover and focus
       BEFORE its click, so leaving them on would open the panel first and the click
       handler below would then read it as already-open, making the first tap on a split
       word navigate (and a plain trigger open then instantly close again). On a coarse
       pointer the tap handlers below are the whole story. */
    document.addEventListener('mouseover', function (e) {
      if (isMobile() || isCoarse()) return;
      var t = e.target.closest && e.target.closest('.vms-mega__trigger, .vms-mega__chev');
      if (!t) {
        // the pointer reached the open panel: a pending lower-row open is not wanted
        if (pendingTrigger && e.target.closest && e.target.closest('.vms-mega__panel.is-open')) cancelPending();
        return;
      }
      var key = t.getAttribute('data-vms-panel');
      var openExp = document.querySelector('.vms-mega__chev[aria-expanded="true"], .vms-mega__trigger[aria-expanded="true"]');
      if (openExp && openExp.getAttribute('data-vms-panel') !== key) { armPending(t); return; }
      openPanel(key);
      if (!openExp) hoverKey = key;
    });

    // lower-row rest rule: each move over the pending trigger restarts its timer; any
    // move elsewhere cancels it
    document.addEventListener('mousemove', function (e) {
      if (!pendingTrigger) return;
      if (isMobile() || isCoarse()) { cancelPending(); return; }
      var t = e.target.closest && e.target.closest('.vms-mega__trigger, .vms-mega__chev');
      if (t === pendingTrigger) armPending(t); else cancelPending();
    });

    // close the open panel shortly after the pointer leaves the whole menu
    document.addEventListener('mouseout', function (e) {
      if (isMobile()) return;
      var mega = document.querySelector('.vms-mega');
      if (!mega) return;
      var to = e.relatedTarget;
      // leaving the pending lower-row trigger cancels its rest timer
      if (pendingTrigger && !(to && pendingTrigger.contains(to))) cancelPending();
      if (to && mega.contains(to)) return;            // still inside the menu
      clearTimeout(closeTimer);
      closeTimer = setTimeout(closePanels, CLOSE_DELAY);
    });

    // keyboard parity for the rule above: tabbing OUT of the menu (e.g. past
    // the last panel's last link into page content) must also close the open
    // panel, or it stays painted over the content that now has focus
    // (WCAG 2.4.11 Focus Not Obscured; audit 2026-07-16).
    document.addEventListener('focusout', function (e) {
      if (isMobile()) return;
      var mega = document.querySelector('.vms-mega');
      if (!mega || !mega.contains(e.target)) return;  // focus was not in the menu
      var to = e.relatedTarget;
      if (to && mega.contains(to)) return;            // still inside the menu
      clearTimeout(closeTimer);
      closeTimer = setTimeout(closePanels, CLOSE_DELAY);
    });

    // PHONE DRAWER, keyboard parity (design review H-3, 2026-09-22). The drawer is an APG
    // disclosure, not a modal, so Tab may leave it; when focus moves from inside the header
    // to an element OUTSIDE it (the page, not the relocated off-hours modal) while the
    // drawer is open, close the drawer the way Esc does (panels shut, burger
    // aria-expanded false, scroll lock released) but leave focus where the user put it.
    // A null relatedTarget (window blur, a click on nothing focusable) changes nothing.
    // Drawer only: desktop and every desktop listener above are untouched.
    document.addEventListener('focusout', function (e) {
      if (!isMobile()) return;
      var h = header();
      if (!h || !h.classList.contains('is-menu-open') || !h.contains(e.target)) return;
      var to = e.relatedTarget;
      if (!to || h.contains(to)) return;
      var modal = modalEl();
      if (modal && modal.contains(to)) return;
      // closePanels() without its focus bounce: focus has already gone to the page
      cancelPending();
      clearTimeout(closeTimer);
      expanders().forEach(function (t) { setExp(t, false); });
      panels().forEach(function (p) { p.classList.remove('is-open'); });
      document.body.classList.remove('vms-mega-open');
      h.classList.remove('is-menu-open');
      var bg = h.querySelector('.vms-header__burger');
      if (bg) bg.setAttribute('aria-expanded', 'false');
      lockScroll();
    });

    document.addEventListener('click', function (e) {
      var tEl = e.target.closest ? e.target : null;

      // nav trigger - toggle a panel. ONLY the <button> triggers; a childless
      // top-level category renders as a real <a class="vms-mega__trigger--link">
      // and must navigate natively (incl. cmd/ctrl/middle-click), so let it fall through.
      var trigger = tEl && tEl.closest('.vms-mega__trigger');
      if (trigger && trigger.tagName === 'BUTTON') {
        e.preventDefault();
        var key = trigger.getAttribute('data-vms-panel');
        if (isMobile()) { toggleAccordion(key); return; }
        clickToggle(trigger, key);
        return;
      }

      var backBtn = tEl && tEl.closest('.vms-mega__back');
      if (backBtn) {
        e.preventDefault();
        var backKey = backBtn.getAttribute('data-vms-panel');
        toggleAccordion(backKey);
        var backTo = expanderFor(backKey);
        if (backTo) backTo.focus();
        return;
      }

      // split trigger, chevron half: toggles the panel on every device
      var chev = tEl && tEl.closest('.vms-mega__chev');
      if (chev) {
        e.preventDefault();
        var chevKey = chev.getAttribute('data-vms-panel');
        if (isMobile()) { toggleAccordion(chevKey); return; }
        clickToggle(chev, chevKey);
        return;
      }

      // split trigger, linked word half. In the drawer, and on any fine pointer (where a
      // hover has already opened the panel), it just navigates. On a coarse pointer at
      // desktop widths there is no hover, so the first tap opens the panel and the second
      // follows the link. Modified clicks (open in a new tab) always navigate.
      if (trigger && trigger.classList.contains('vms-mega__word')) {
        if (isMobile() || !isCoarse()) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var wordKey = trigger.getAttribute('data-vms-panel');
        var wordExp = expanderFor(wordKey);
        if (wordExp && wordExp.getAttribute('aria-expanded') === 'true') return;
        e.preventDefault();
        openPanel(wordKey);
        return;
      }

      // drawer sub-accordion inside a panel (the guided ceiling panel's last two zones).
      // Independent toggles: opening one does not close the other.
      var subToggle = tEl && tEl.closest('.vms-mega__subtoggle');
      if (subToggle) {
        e.preventDefault();
        subToggle.setAttribute('aria-expanded', subToggle.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
        return;
      }

      // scrim - close
      if (tEl && tEl.closest('.vms-mega__scrim')) { closePanels(); return; }

      // mobile hamburger - toggle drawer
      var burger = tEl && tEl.closest('.vms-header__burger');
      if (burger) {
        e.preventDefault();
        var h = header();
        if (h) {
          setHeaderHeight();
          var nowOpen = h.classList.toggle('is-menu-open');
          burger.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
          if (nowOpen) {
            h.classList.remove('is-search-open'); // mutually exclusive with the search row
            var stBtn = h.querySelector('.vms-header__search-toggle');
            if (stBtn) stBtn.setAttribute('aria-expanded', 'false');
            // move focus into the drawer so keyboard/SR users land on the nav (APG disclosure)
            var firstTrigger = h.querySelector('.vms-mega__trigger');
            if (firstTrigger) firstTrigger.focus();
          } else {
            closePanels();
            burger.focus(); // return focus to the invoker on close
          }
          lockScroll();
        }
        return;
      }

      // mobile search toggle
      var st = tEl && tEl.closest('.vms-header__search-toggle');
      if (st) {
        e.preventDefault();
        var hh = header();
        if (hh) {
          var so = hh.classList.toggle('is-search-open');
          st.setAttribute('aria-expanded', so ? 'true' : 'false');
          if (so) {
            if (hh.classList.contains('is-menu-open')) { // mutually exclusive with the drawer
              hh.classList.remove('is-menu-open');
              var bg = hh.querySelector('.vms-header__burger');
              if (bg) bg.setAttribute('aria-expanded', 'false');
              closePanels();
              lockScroll();
            }
            var input = hh.querySelector('.vms-header__search-input');
            if (input) setTimeout(function () { input.focus(); }, 60);
          } else if (hh.contains(document.activeElement)) {
            st.focus(); // closing: don't strand focus in the hidden field
          }
        }
        return;
      }

      // phone / "Talk to an expert" - gate the tel: link
      var phone = tEl && tEl.closest('.vms-expert');
      if (phone && phone.getAttribute('href') && phone.getAttribute('href').indexOf('tel:') === 0) {
        if (!isOpenNow(phone)) { e.preventDefault(); openModal(); }
        return;
      }

      /* Every OTHER tel: link on the page gets the same gate: contact page,
       * footer, body copy, anything added later. The header link is handled
       * above and returns before reaching here. Guarded so that a missing
       * schedule or a missing modal lets the call go through. */
      var telLink = tEl && tEl.closest('a[href^="tel:"]');
      if (telLink) {
        var src = hoursSource();
        if (src && modalEl() && !isOpenNow(src)) { e.preventDefault(); openModal(); }
        return;
      }

      // off-hours modal: click the backdrop or the close button to dismiss
      var modal = modalEl();
      if (modal && modal.classList.contains('is-open')) {
        if (tEl && tEl.closest('.vms-hours-modal__close')) { closeModal(); return; }
        if (e.target === modal) { closeModal(); return; }
      }
    });

    // mega menu review 2026-09-27 item 9: arrow keys on the desktop nav
    document.addEventListener('keydown', function (e) {
      var k = e.key;
      if (isMobile() || e.altKey || e.ctrlKey || e.metaKey || ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(k) < 0) return;
      var nav = document.querySelector('.vms-mega__nav');
      var a = document.activeElement;
      if (!nav || !a || !nav.contains(a)) return;
      var panel = a.closest('.vms-mega__panel');
      if (panel) {
        if (k === 'ArrowLeft' || k === 'ArrowRight') return;
        var items = focusable(panel), i = items.indexOf(a);
        e.preventDefault();
        if (k === 'Home') i = 0; else if (k === 'End') i = items.length - 1;
        else if (k === 'ArrowDown') i = Math.min(i + 1, items.length - 1);
        else if (i <= 0) { var ex = expanderFor(panel.getAttribute('data-vms-panel')); if (ex) ex.focus(); return; }
        else i = i - 1;
        if (items[i]) items[i].focus();
        return;
      }
      var tops = focusable(nav).filter(function (n) { return !n.closest('.vms-mega__panel'); });
      var j = tops.indexOf(a);
      if (j < 0) return;
      var key = a.getAttribute('data-vms-panel');
      if (k === 'ArrowDown') {
        if (!key) return;
        e.preventDefault();
        openPanel(key);
        var p = document.querySelector('.vms-mega__panel[data-vms-panel="' + cssEscape(key) + '"]');
        var first = p && focusable(p)[0];
        if (first) first.focus();
        return;
      }
      if (k === 'ArrowUp') return;
      e.preventDefault();
      j = k === 'Home' ? 0 : k === 'End' ? tops.length - 1 : k === 'ArrowRight' ? Math.min(j + 1, tops.length - 1) : Math.max(j - 1, 0);
      var next = tops[j];
      if (document.querySelector('.vms-mega__panel.is-open') && next.getAttribute('data-vms-panel') !== key) closePanels();
      next.focus();
    });

    // keyboard: Esc closes whatever is open; Tab is trapped inside the modal
    document.addEventListener('keydown', function (e) {
      var modal = modalEl();
      if (modal && modal.classList.contains('is-open')) {
        if (e.key === 'Escape') { closeModal(); return; }
        if (e.key === 'Tab') { trapTab(e); return; }
      }
      if (e.key === 'Escape') {
        var h = header();
        var drawerWasOpen = h && h.classList.contains('is-menu-open');
        // split triggers hand focus back to the CHEVRON, which is the control that opened
        // the panel; plain triggers are their own button
        var openTrigger = document.querySelector('.vms-mega__chev[aria-expanded="true"], .vms-mega__trigger[aria-expanded="true"]');
        var inPanel = document.activeElement && document.activeElement.closest && document.activeElement.closest('.vms-mega__panel.is-open');
        var searchEl = h && h.querySelector('.vms-header__search');
        var inSearch = h && h.classList.contains('is-search-open') && searchEl && searchEl.contains(document.activeElement);
        var searchToggle = h && h.querySelector('.vms-header__search-toggle');
        var burger = h && h.querySelector('.vms-header__burger');
        closePanels();
        if (h) { h.classList.remove('is-menu-open'); h.classList.remove('is-search-open'); }
        // closing by class removal must also reset the toggles' aria-expanded (a11y P-3,
        // fixed 2026-08-05: Esc left the burger announcing expanded on a closed drawer)
        if (burger) burger.setAttribute('aria-expanded', 'false');
        if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
        // mobile: the trigger is inside the now-hidden drawer, so return focus to the (visible) burger
        if (drawerWasOpen && burger) burger.focus();
        else if (inPanel && openTrigger) openTrigger.focus();    // desktop: trigger stays visible
        else if (inSearch && searchToggle) searchToggle.focus(); // or to the search toggle
        lockScroll();
      }
    });

    // crossing the mobile/desktop boundary: reset all open states cleanly
    window.matchMedia(MOBILE_MQ).addEventListener('change', function () {
      closePanels();
      var h = header();
      if (h) {
        h.classList.remove('is-menu-open');
        h.classList.remove('is-search-open');
        // same P-3 defect class as the Escape branch: the toggles must agree
        var bg = h.querySelector('.vms-header__burger');
        var st = h.querySelector('.vms-header__search-toggle');
        if (bg) bg.setAttribute('aria-expanded', 'false');
        if (st) st.setAttribute('aria-expanded', 'false');
      }
      lockScroll();
    });
  }

  /* Dawn's StickyHeader (which sets --header-height) is gone with its section,
     so publish the header height ourselves for sticky offsets / scroll-margins. */
  function setHeaderHeight() {
    var h = document.querySelector('.vms-header');
    if (!h) return;
    var bar = h.querySelector('.vms-header__bar');
    /* Batch the layout READS first, then the WRITES, so we never force a reflow
       between a style write and the next offsetHeight read (this runs on every
       ResizeObserver/resize frame). */
    var headerH = h.getBoundingClientRect().height; // sticky header round 2026-09-28: unrounded, it is the desktop stuck height
    var barH = bar ? bar.getBoundingClientRect().height : 0; // sticky header round 2026-09-28: unrounded, the phone stick offset uses it
    var barB = bar ? Math.max(0, Math.ceil(bar.getBoundingClientRect().bottom)) : 0;
    var s = document.documentElement.style;
    s.setProperty('--header-height', headerH + 'px');
    // mega menu review 2026-09-27 item 3: the drawer starts at the bar's bottom edge
    if (bar) s.setProperty('--vms-bar-bottom', barB + 'px');
    /* The sticky BAR height is published separately and is STABLE whether or not the
       mobile drawer is open, so the drawer sizes its max-height against THIS, not
       --header-height (which includes the open drawer). Sizing the drawer against a value
       that grows with the drawer caused a ResizeObserver feedback loop (open tall panel ->
       header grows -> drawer max-height shrinks -> header shrinks -> repeat = flicker). */
    if (bar) s.setProperty('--vms-bar-h', barH + 'px');
  }

  var PHONE_MQ = '(max-width: 749px)';
  function syncSearchPlaceholder() {
    var input = document.getElementById('vms-header-search');
    if (!input) return;
    var phone = input.getAttribute('data-placeholder-phone');
    var full = input.getAttribute('data-placeholder-full');
    if (!phone || !full) return;
    input.setAttribute('placeholder', window.matchMedia(PHONE_MQ).matches ? phone : full);
  }

  function init() {
    relocateModal();
    setHeaderHeight();
    bindOnce();
    syncSearchPlaceholder();
    window.matchMedia(PHONE_MQ).addEventListener('change', syncSearchPlaceholder);
    if ('ResizeObserver' in window) {
      var h = document.querySelector('.vms-header');
      if (h) new ResizeObserver(setHeaderHeight).observe(h);
    }
    window.addEventListener('resize', setHeaderHeight);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // theme editor: a header section reload re-creates the modal/markup in place
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.vms-header')) {
      relocateModal();
      setHeaderHeight(); // height may change when header settings are edited
      syncSearchPlaceholder();
    }
  });
  // catch late layout (logo image load) for an accurate --header-height
  window.addEventListener('load', setHeaderHeight);
})();
